import { useState, useEffect, useMemo, useRef } from "react";
import Spinner from "../../../globalComponents/ui/Spinner";
import { assetScopeLabel, bucketOf, titleExistsIn } from "./helpers";

export function IconBtn({ icon, variant, label, onClick }) {
  return (
    <button
      type="button"
      className={`kpa-icon-btn kpa-icon-btn--${variant}`}
      title={label}
      aria-label={label}
      onClick={e => { e.stopPropagation(); onClick(); }}
    >
      <i className={`fas ${icon}`} aria-hidden="true" />
    </button>
  );
}

/** Read-only asset chip row. */
export function AssetReadRow({ asset, sources, onEdit, onDelete, disabled, deleting, canUpdate, canDelete }) {
  const { hasLocation, unitLabel } = assetScopeLabel(asset, sources);

  return (
    <div className={`kpa-asset-row${deleting ? " kpa-asset-row--deleting" : ""}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className={`kpa-asset-title${deleting ? " kpa-asset-title--deleting" : ""}`}>
          {asset.assetTitle}
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 3 }}>
          <span className={`kpa-tag ${hasLocation ? "kpa-tag--green" : "kpa-tag--amber"}`}>
            <i className={`fas ${hasLocation ? "fa-map-marker-alt" : "fa-globe"}`} style={{ marginRight: 3, fontSize: 9 }} aria-hidden="true" />
            {unitLabel}
          </span>
        </div>
      </div>
      {deleting ? (
        <Spinner size={14} color="#ef4444" />
      ) : (
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {!disabled && canUpdate && (
            <IconBtn icon="fa-pen" variant="edit" label={`Edit ${asset.assetTitle}`} onClick={() => onEdit(asset)} />
          )}
          {!disabled && canDelete && (
            <IconBtn icon="fa-trash" variant="delete" label={`Delete ${asset.assetTitle}`} onClick={() => onDelete(asset)} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Inline edit row (edit name only, unit fixed).
 *
 * The By KPI view passes no `crossKpiMatches` — rename touches that KPI only.
 * The By Asset view passes the other KPIs holding the asset, and the rename
 * always applies to all of them (title conflicts are skipped and reported).
 */
export function AssetEditRow({ asset, sources, allAssets, crossKpiMatches = [], onSave, onCancel }) {
  const [title, setTitle] = useState(asset.assetTitle ?? "");
  const [dupError, setDupError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const { hasLocation, unitLabel } = assetScopeLabel(asset, sources);

  /**
   * Other KPIs where the new name would collide (excluding the matched asset
   * itself, which is the one being renamed there).
   */
  const conflictedMatches = useMemo(
    () => crossKpiMatches.filter(m => titleExistsIn(m.assets, title, bucketOf(asset), m.id)),
    [crossKpiMatches, title, asset],
  );

  const handleSave = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    if (titleExistsIn(allAssets, trimmed, bucketOf(asset), asset.id)) {
      setDupError(
        asset.sourceId == null
          ? `A global asset named "${trimmed}" already exists for this KPI.`
          : `An asset named "${trimmed}" already exists for this location.`
      );
      return;
    }
    const extraIds = crossKpiMatches
      .filter(m => !conflictedMatches.includes(m))
      .map(m => m.id);
    onSave(trimmed, extraIds);
  };

  const isValid = title.trim() && !dupError;

  return (
    <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--kpa-slate-100)", background: "var(--kpa-blue-50)" }}>
      <div style={{ fontSize: 10, color: "var(--kpa-blue-700)", fontWeight: 600, marginBottom: 6 }}>
        <i className={`fas ${hasLocation ? "fa-map-marker-alt" : "fa-globe"}`} style={{ marginRight: 4 }} aria-hidden="true" />
        {unitLabel}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <input
            ref={inputRef}
            className={`kpa-input${dupError ? " kpa-input--error" : title.trim() ? " kpa-input--active" : ""}`}
            value={title}
            onChange={e => { setTitle(e.target.value); if (dupError) setDupError(""); }}
            placeholder="Asset name…"
            aria-label="Asset name"
            onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") onCancel(); }}
          />
          {dupError && (
            <div className="kpa-error-box" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{dupError}</span>
            </div>
          )}
          {crossKpiMatches.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, color: "var(--kpa-slate-600)" }}>
                <i className="fas fa-info-circle" style={{ marginRight: 4, color: "var(--kpa-primary)" }} aria-hidden="true" />
                Renames this asset in all {crossKpiMatches.length + 1} KPIs holding it.
              </div>
              {conflictedMatches.length > 0 && (
                <div className="kpa-warn-box" style={{ marginTop: 4, marginBottom: 0 }} role="status">
                  <i className="fas fa-info-circle" aria-hidden="true" />
                  <span>
                    Will be skipped for {conflictedMatches.map(m => m.kpiTitle).join(", ")} — that name already exists there.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <button type="button" className="kpa-btn kpa-btn--ghost" style={{ alignSelf: "flex-start" }} onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className="kpa-btn kpa-btn--primary"
          style={{ alignSelf: "flex-start" }}
          onClick={handleSave}
          disabled={!isValid}
        >
          <i className="fas fa-check" style={{ marginRight: 4 }} aria-hidden="true" />Save
        </button>
      </div>
    </div>
  );
}

/** Delete confirm strip (asset view bulk removals). */
export function DeleteConfirmRow({ message, confirmLabel, onCancel, onConfirm }) {
  return (
    <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--kpa-slate-100)", background: "var(--kpa-red-50)" }}>
      <div style={{ fontSize: 11.5, color: "var(--kpa-red-800)", fontWeight: 600, marginBottom: 8 }}>
        <i className="fas fa-exclamation-triangle" style={{ marginRight: 5 }} aria-hidden="true" />
        {message}
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="kpa-btn kpa-btn--ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="kpa-btn kpa-btn--danger" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  );
}
